import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardMedia,
  IconButton,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const CoverImageUploadDialog = ({ 
  open, 
  onClose, 
  onUpload, 
  currentImage = null,
  planTitle = "Travel Plan"
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Available preset cover images
  const presetImages = [
    { src: '/covers/adventure.png', label: 'Adventure' },
    { src: '/covers/beach.png', label: 'Beach' },
    { src: '/covers/city.png', label: 'City' },
    { src: '/covers/food_shopping.png', label: 'Food & Shopping' },
    { src: '/covers/history.png', label: 'History' },
    { src: '/covers/nature.png', label: 'Nature' }
  ];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      setSelectedFile(file);
      setError(null);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePresetSelect = (imageSrc) => {
    setSelectedFile(null);
    setPreview(imageSrc);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile && !preview) {
      setError('Please select an image');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      let imageUrl = preview;

      // If it's a file upload, upload to the server
      if (selectedFile) {
        // Use the actual file upload
        imageUrl = 'FILE_UPLOAD'; // Special marker for file upload
      }

      await onUpload(imageUrl, selectedFile);
      handleClose();
    } catch (error) {
      console.error('Upload failed:', error);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '500px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div">
          Choose Cover Image
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Current Preview */}
          {(preview || currentImage) && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Preview
              </Typography>
              <Card sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={preview || currentImage}
                  alt="Cover preview"
                  sx={{ objectFit: 'cover' }}
                />
                <IconButton
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)'
                    }
                  }}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Card>
            </Box>
          )}

          {/* Upload Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Upload Custom Image
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: 'center',
                borderStyle: 'dashed',
                borderWidth: 2,
                borderColor: 'grey.300',
                backgroundColor: 'grey.50',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.50'
                }
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.600', mb: 1 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Click to upload
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Support: JPG, PNG, GIF (Max 5MB)
              </Typography>
            </Paper>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </Box>

          {/* Preset Images */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Choose from Presets
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: 2
            }}>
              {presetImages.map((preset) => (
                <Card
                  key={preset.src}
                  sx={{
                    cursor: 'pointer',
                    border: preview === preset.src ? '2px solid' : '1px solid',
                    borderColor: preview === preset.src ? 'primary.main' : 'grey.300',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => handlePresetSelect(preset.src)}
                >
                  <CardMedia
                    component="img"
                    height="80"
                    image={preset.src}
                    alt={preset.label}
                    sx={{ objectFit: 'cover' }}
                  />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      p: 1, 
                      textAlign: 'center',
                      display: 'block',
                      fontSize: '0.75rem'
                    }}
                  >
                    {preset.label}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={(!selectedFile && !preview) || uploading}
          startIcon={uploading ? <CircularProgress size={20} /> : <ImageIcon />}
        >
          {uploading ? 'Applying...' : 'Apply Cover Image'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoverImageUploadDialog;